/**
 * Tag color system — Inspired Edge brand palette + theme-aware chip styling.
 *
 * Tags store a free-form `color` string (a hex value, e.g. "#E9445D"). This
 * module owns the curated on-brand palette used by the inline create flow and
 * the admin swatch picker, plus helpers that turn a stored hex into a *legible*
 * chip in BOTH the dark (canonical) and light themes.
 *
 * Legibility trick: a single fixed hue can't stay readable as text across a
 * near-white and a deep-navy background — the design system already works
 * around this for `gold` with `text-gold-strong` / `dark:text-gold`. So chips
 * use a translucent tinted fill + a hue border (theme-agnostic; a tint reads on
 * either surface) and pull the *text* toward the active theme's `--foreground`
 * via `color-mix`. Browsers without `color-mix` simply ignore that one
 * declaration and inherit the foreground — still legible, never broken.
 */
import type { CSSProperties } from "react";

export interface TagSwatch {
  /** Stable key + accessible label, e.g. "coral". */
  name: string;
  /** Hex value stored in `relay.tags.color`. */
  value: string;
}

/**
 * On-brand swatches. coral / gold / steel mirror the brand hexes documented in
 * index.css; lavender extends the navy-indigo family. The rest are harmonious,
 * mid-lightness hues spread around the wheel so cycled defaults stay visually
 * distinct. Mid-lightness + saturated means each reads as accent text on a
 * faint tint in either theme once nudged toward `--foreground`.
 */
export const TAG_PALETTE: readonly TagSwatch[] = [
  { name: "coral", value: "#E9445D" },
  { name: "gold", value: "#F4A162" },
  { name: "steel", value: "#82A6C9" },
  { name: "lavender", value: "#A78BFA" },
  { name: "emerald", value: "#34C79A" },
  { name: "sky", value: "#5BA9F4" },
  { name: "rose", value: "#EC6FA6" },
  { name: "amber", value: "#E0A93B" },
] as const;

/** Fallback when no color is stored and no seed is available. */
export const DEFAULT_TAG_COLOR = TAG_PALETTE[0].value;

/** Highest raw-hue percentage allowed in chip text before mixing toward the
 *  theme foreground. Every curated palette swatch clears WCAG AA at this value;
 *  arbitrary custom colors are reduced from here as needed to keep contrast. */
const MAX_TEXT_HUE_RATIO = 55;

/** Minimum WCAG AA contrast for normal-sized text. */
const MIN_TEXT_CONTRAST = 4.5;

/** Chip tint opacity used in tagChipStyle. Kept in sync with the contrast math. */
const CHIP_BACKGROUND_ALPHA = 0.15;

/**
 * Real theme tokens from index.css as RGB triples:
 *   light: --background 240 20% 99%, --foreground 240 28% 12%
 *   dark:  --background 240 28% 9%,  --foreground 0 0% 98%
 */
const THEME_CONTRAST_SAMPLES = [
  { surface: [252, 252, 253] as const, foreground: [22, 22, 39] as const },
  { surface: [17, 17, 29] as const, foreground: [250, 250, 250] as const },
] as const;

/**
 * Deterministically pick a palette color from a seed (a tag name or slug) so
 * the same name always maps to the same color and different names spread across
 * the palette. Stable across reloads and devices — nicer than a random cycle,
 * and lets legacy uncolored tags render with a consistent hue without a
 * data backfill.
 */
export function defaultTagColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length].value;
}

/** Parse #rgb / #rrggbb (with or without leading #) into [r,g,b]; null if not a hex. */
function parseHex(hex: string): [number, number, number] | null {
  const m = hex.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(m)) {
    return [m[0], m[1], m[2]].map((c) => parseInt(c + c, 16)) as [number, number, number];
  }
  if (/^[0-9a-fA-F]{6}$/.test(m)) {
    return [m.slice(0, 2), m.slice(2, 4), m.slice(4, 6)].map((c) => parseInt(c, 16)) as [
      number,
      number,
      number,
    ];
  }
  return null;
}

function rgba(hex: string, alpha: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return `rgba(128, 128, 128, ${alpha})`;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

function formatHex6([r, g, b]: [number, number, number]): string {
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function mixRgb(
  first: readonly [number, number, number],
  second: readonly [number, number, number],
  firstRatio: number,
): [number, number, number] {
  const ratio = firstRatio / 100;
  return first.map((channel, index) =>
    Math.round(channel * ratio + second[index] * (1 - ratio)),
  ) as [number, number, number];
}

function relativeLuminance(rgb: readonly [number, number, number]): number {
  const [r, g, b] = rgb.map((channel) => {
    const v = channel / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): number {
  const lighter = Math.max(relativeLuminance(a), relativeLuminance(b));
  const darker = Math.min(relativeLuminance(a), relativeLuminance(b));
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Highest hue ratio (≤ MAX_TEXT_HUE_RATIO) at which `hex` chip text still clears
 * AA against the tinted chip background in BOTH themes. Palette hues keep the
 * full ratio; problematic customs (e.g. near-white in light mode) drop toward
 * 0, where the text is effectively the theme foreground. The contrast check
 * approximates the rendered oklab mix with an sRGB mix — close, and erring
 * toward more foreground is the safe direction.
 */
function textHueRatio(hex: string): number {
  const hue = parseHex(hex);
  if (!hue) return 0;
  for (let ratio = MAX_TEXT_HUE_RATIO; ratio >= 0; ratio -= 1) {
    const clearsBothThemes = THEME_CONTRAST_SAMPLES.every(({ surface, foreground }) => {
      const chipBackground = mixRgb(hue, surface, CHIP_BACKGROUND_ALPHA * 100);
      const chipText = mixRgb(hue, foreground, ratio);
      return contrastRatio(chipText, chipBackground) >= MIN_TEXT_CONTRAST;
    });
    if (clearsBothThemes) return ratio;
  }
  return 0;
}

/**
 * Coerce a stored color into a usable hex. Falls back to a deterministic
 * palette color derived from `seed` (so legacy `color: null` tags still render
 * a stable, distinct hue) or the brand default when no seed is given.
 */
export function resolveTagColor(color: string | null | undefined, seed?: string): string {
  const rgb = color ? parseHex(color) : null;
  if (rgb) return formatHex6(rgb);
  if (seed) return defaultTagColor(seed);
  return DEFAULT_TAG_COLOR;
}

/** True when `color` is a valid hex string we can render. */
export function isValidTagColor(color: string | null | undefined): boolean {
  return !!color && !!parseHex(color);
}

/**
 * Inline styles for a tinted tag chip that stays legible in both themes.
 * `seed` (the tag name) gives uncolored tags a stable fallback hue.
 */
export function tagChipStyle(color: string | null | undefined, seed?: string): CSSProperties {
  const hex = resolveTagColor(color, seed);
  const hueRatio = textHueRatio(hex);
  return {
    backgroundColor: rgba(hex, CHIP_BACKGROUND_ALPHA),
    borderColor: rgba(hex, 0.42),
    // Theme-aware ink: keep as much hue as still clears AA against the tinted
    // background in both themes (reduced for arbitrary custom colors), then mix
    // the rest toward the current foreground. Unsupported `color-mix` →
    // declaration ignored → text inherits the (legible) foreground.
    color: `color-mix(in oklab, ${hex} ${hueRatio}%, hsl(var(--foreground)))`,
  };
}

/** Solid hue for a dot / swatch indicator (e.g. dropdown rows). */
export function tagDotColor(color: string | null | undefined, seed?: string): string {
  return resolveTagColor(color, seed);
}

/**
 * Resolve a color to a 6-digit `#rrggbb` string for a native
 * `<input type="color">`, which per the HTML spec rejects 3-digit and non-hex
 * values (silently coercing them to #000000). CSS accepts the raw value fine,
 * so this is only needed where the value feeds that control.
 */
export function toHex6(color: string | null | undefined, seed?: string): string {
  return formatHex6(parseHex(resolveTagColor(color, seed)) ?? [0, 0, 0]);
}
